// Fetch wrapper. Auth token comes from localStorage (synced by AuthProvider).

const TOKEN_KEY = "mc.access_token";
const REFRESH_KEY = "mc.refresh_token";
const USER_KEY = "mc.user";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export const tokens = {
  getAccess: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  setTokens: (access: string, refresh: string) => {
    localStorage.setItem(TOKEN_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  setUser: (u: unknown) => localStorage.setItem(USER_KEY, JSON.stringify(u)),
  getUser: <T,>() => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as T) : null;
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

type FetchOpts = Omit<RequestInit, "body"> & {
  body?: unknown;
  skipAuth?: boolean;
};

export async function request<T = unknown>(path: string, opts: FetchOpts = {}): Promise<T> {
  const isAbsolute = path.startsWith("http://") || path.startsWith("https://");
  const url = isAbsolute ? path : `/api${path}`;
  const headers = new Headers(opts.headers as HeadersInit | undefined);
  const hasJsonBody = opts.body !== undefined && !(opts.body instanceof FormData);
  if (hasJsonBody && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const token = tokens.getAccess();
  if (token && !opts.skipAuth && !isAbsolute) headers.set("Authorization", `Bearer ${token}`);

  const init: RequestInit = {
    ...opts,
    headers,
    body: hasJsonBody && !(opts.body instanceof FormData) ? JSON.stringify(opts.body) : (opts.body as BodyInit | undefined),
  };

  const res = await fetch(url, init);

  if (res.status === 401 && !opts.skipAuth) {
    tokens.clear();
    if (location.pathname !== "/login") location.href = "/login";
    throw new ApiError(401, "Unauthorized");
  }
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const data = await res.json();
      detail = data.detail || JSON.stringify(data);
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") || "";
  return (ct.includes("application/json") ? res.json() : res.text()) as Promise<T>;
}

// Download a binary as a Blob with auth — used for certificate PDFs.
export async function downloadBlob(path: string): Promise<Blob> {
  const token = tokens.getAccess();
  const res = await fetch(`/api${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return res.blob();
}
